import {useEffect, useRef} from 'react'

import {openAlert} from '../../../../../../../../../_metronic/elements/NotificationAlert'
import {useMapLayersV2} from '../../../../../../Map/MapLayersV2Context'
import {useMapLegendContext} from '../../../Helpers/MapLegend'
import {useViewImageContext} from '../../ViewImageContext'
import {viewImageState} from '../../ViewImageState'
import * as GisDataAdapter from '../../../../../services/GisDataAdapter'

const parseBlobJson = async (blob) => {
  const text = await blob.text()
  return JSON.parse(text)
}

const YieldMap = ({land, yieldMapId}) => {
  const mapLayers = useMapLayersV2()
  const mapLegendContext = useMapLegendContext()
  const viewImageContext = useViewImageContext()
  const executing = useRef(false)
  const mapLayersRef = useRef(mapLayers)
  const mapLegendContextRef = useRef(mapLegendContext)
  const viewImageContextRef = useRef(viewImageContext)
  const objectUrlRef = useRef(null)
  const layerId = `yield_map-${land.land_id}`

  mapLayersRef.current = mapLayers
  mapLegendContextRef.current = mapLegendContext
  viewImageContextRef.current = viewImageContext

  useEffect(() => {
    let cancelled = false

    const revokeObjectUrl = () => {
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current)
        objectUrlRef.current = null
      }
    }

    const loadYieldMap = async () => {
      if (executing.current || !yieldMapId) {
        return
      }

      executing.current = true
      viewImageContextRef.current.setLoading(true)

      try {
        const [pngResponse, gridResponse] = await Promise.all([
          GisDataAdapter.downloadLandYieldMap(land.land_id, yieldMapId, {driver: 'PNG'}),
          GisDataAdapter.downloadLandYieldMap(land.land_id, yieldMapId, {driver: 'GRID'}),
        ])

        if (!pngResponse?.ok || !gridResponse?.ok) {
          throw new Error('No pudimos cargar el mapa de rendimiento')
        }

        const grid = await parseBlobJson(gridResponse.data)
        const bbox = grid?.bbox

        if (!Array.isArray(bbox) || bbox.length !== 4) {
          throw new Error('El mapa de rendimiento no tiene bbox valido')
        }

        if (cancelled) {
          return
        }

        revokeObjectUrl()
        const imageUrl = URL.createObjectURL(pngResponse.data)
        objectUrlRef.current = imageUrl

        mapLayersRef.current.fit(land.land_id)
        mapLayersRef.current.land.addImage({
          landId: land.land_id,
          imageType: 'yield_map',
          imageUrl,
          bbox,
          layerId,
          opacity: 1,
        })
        viewImageState.addLandYieldMap(land.land_id, yieldMapId, grid)
        mapLegendContextRef.current.changeBasicLegend(land.land_id, {
          ...(grid.legend || {}),
          type: 'yield_map',
        })
      } catch (error) {
        if (!cancelled) {
          console.error(error)
          openAlert(
            'danger',
            'Error',
            'No pudimos cargar el mapa de rendimiento de este lote.'
          )
          mapLayersRef.current.land.deleteImage({
            landId: land.land_id,
            imageType: 'yield_map',
            layerId,
          })
          mapLegendContextRef.current.clearBasicLegend(land.land_id)
          viewImageState.deleteLandYieldMap(land.land_id)
        }
      } finally {
        if (!cancelled) {
          viewImageContextRef.current.setLoading(false)
        }
        executing.current = false
      }
    }

    loadYieldMap()

    return () => {
      cancelled = true
      executing.current = false
      viewImageContextRef.current.setLoading(false)
      mapLegendContextRef.current.clearBasicLegend(land.land_id)
      mapLayersRef.current.land.deleteImage({
        landId: land.land_id,
        imageType: 'yield_map',
        layerId,
      })
      revokeObjectUrl()
    }
  }, [land.land_id, layerId, yieldMapId])

  return null
}

export default YieldMap
